import React, { useRef } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { vars } from '@machobear/design-system/css'

import { breakpoints } from '@/styles'
import {
  Bus,
  Coffee,
  GraduationCap,
  ShoppingBag,
  ShoppingCart,
  Trees,
  Utensils
} from '@/components/icons'

type Props = {
  currentTab: number
  setCurrentTab: (tab: number) => void
  setActiveId: (id: number | null) => void
  hideSchools: boolean
}

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  max-width: 100%;
  border: none;
  border-bottom: 1px solid ${vars.borderColor.neutralLight};
  ::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  @media only screen and (max-width: ${breakpoints.desktop}) {
    justify-content: space-between;
    gap: 0;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 32px;
    overflow-x: auto;
    overflow-y: visible;
    gap: 0px;
    border: none;
  }
`

const TabButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  font-family: Inter;
  font-weight: ${props => (props.isActive ? 500 : 400)};
  font-size: 16px;
  color: ${props =>
    props.isActive
      ? vars.foregroundColor.formAccent
      : vars.foregroundColor.tertiary};
  gap: 8px;
  border: none;
  position: relative;
  margin-bottom: -1px;
  background-color: transparent;
  min-width: max-content;
  border-bottom: ${props =>
    props.isActive
      ? `2px solid ${vars.borderColor.formAccent}`
      : `2px solid transparent`};

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-bottom: 0;
    padding: 0 10px;
  }
`

const TabBar = ({
  currentTab,
  setCurrentTab,
  setActiveId,
  hideSchools
}: Props) => {
  const tabBarRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('poi')
  const handleTabClick = (tab: number) => {
    const scrollAmount = tab * 100
    setCurrentTab(tab)
    setActiveId(null)
    tabBarRef.current?.scrollTo({ left: scrollAmount, behavior: 'smooth' })
  }

  return (
    <Row ref={tabBarRef}>
      <TabButton
        onClick={() => handleTabClick(0)}
        isActive={currentTab === 0}
        id="listing-detail-poi-transit"
      >
        <Bus width={18} />
        {t('poi:b.listings.poi.transit', 'Transit')}
      </TabButton>
      {!hideSchools && (
        <TabButton
          onClick={() => handleTabClick(1)}
          isActive={currentTab === 1}
          id="listing-detail-poi-school"
        >
          <GraduationCap width={18} />
          {t('poi:b.listings.poi.school', 'Schools')}
        </TabButton>
      )}
      <TabButton
        onClick={() => handleTabClick(2)}
        isActive={currentTab === 2}
        id="listing-detail-poi-grocery"
      >
        <ShoppingCart width={18} />
        {t('poi:b.listings.poi.groceries', 'Groceries')}
      </TabButton>
      <TabButton
        onClick={() => handleTabClick(3)}
        isActive={currentTab === 3}
        id="listing-detail-poi-cafe"
      >
        <Coffee width={18} />
        {t('poi:b.listings.poi.cafes', 'Cafes')}
      </TabButton>
      <TabButton
        onClick={() => handleTabClick(4)}
        isActive={currentTab === 4}
        id="listing-detail-poi-restaurant"
      >
        <Utensils width={18} />
        {t('poi:b.listings.poi.restaurants', 'Restaurants')}
      </TabButton>
      <TabButton
        onClick={() => handleTabClick(5)}
        isActive={currentTab === 5}
        id="listing-detail-poi-park"
      >
        <Trees width={18} />
        {t('poi:b.listings.poi.parks', 'Parks')}
      </TabButton>
      <TabButton
        onClick={() => handleTabClick(6)}
        isActive={currentTab === 6}
        id="listing-detail-poi-shop"
      >
        <ShoppingBag width={18} />
        {t('poi:b.listings.poi.shopping', 'Shopping')}
      </TabButton>
    </Row>
  )
}

export default TabBar
